import { LAST_LOOP_FRAME, VIDEO_FPS } from '../constants'
import { demuxVideo } from './demux'
import type { DemuxedVideo } from './demux'
import type { FrameSource, SyncDiagnostics } from './source'

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v))

/** Decoded frames held per lane. Frames are large, so this stays small. */
const LANE_CAPACITY = 10

/** Start warming the loop entry this many frames before the seam. */
const SEAM_LEAD_FRAMES = 45

/** Rebuild the decoders if the wanted frame has not arrived within this long. */
const STALL_TIMEOUT_MS = 600

/**
 * One decoder plus the frames it has produced.
 *
 * The scheduler runs two of these and ping-pongs them at the loop seam: while
 * the primary lane plays out the end of the loop, the standby lane is already
 * decoding the loop entry. At the wrap they swap. That is why the loop point
 * never stalls and never needs a seek — the frames are simply already there.
 */
class DecodeLane {
  private decoder: VideoDecoder | null = null
  private readonly frames = new Map<number, VideoFrame>()
  /** Next index into the decode-ordered sample list to feed. */
  private cursor = 0
  private pending = 0
  private failed: Error | null = null

  /** Frame index this lane was last primed at, or -1 if idle. */
  primedAt = -1

  constructor(
    private readonly media: DemuxedVideo,
    private readonly config: VideoDecoderConfig,
  ) {}

  get error(): Error | null {
    return this.failed
  }

  get size(): number {
    return this.frames.size
  }

  has(frameIndex: number): boolean {
    return this.frames.has(frameIndex)
  }

  get(frameIndex: number): VideoFrame | undefined {
    return this.frames.get(frameIndex)
  }

  /**
   * Point this lane at `frameIndex`, discarding anything already decoded.
   * Never throws: a failure leaves the lane unprimed for the scheduler's
   * watchdog to retry.
   */
  prime(frameIndex: number): void {
    this.ensureDecoder()
    const decoder = this.decoder
    if (!decoder) return

    try {
      if (decoder.state === 'configured') decoder.reset()
      decoder.configure(this.config)
    } catch (err) {
      // Most likely a decoder that closed underneath us. Drop it so the next
      // attempt builds a fresh one rather than retrying against a corpse.
      this.failed = err instanceof Error ? err : new Error(String(err))
      this.disposeDecoder()
      this.primedAt = -1
      return
    }

    this.clearFrames()
    this.pending = 0
    this.cursor = this.keyframeCursorFor(frameIndex)
    this.primedAt = frameIndex
  }

  /**
   * Feed encoded chunks until the lane holds frames through `throughFrame` or
   * runs out of capacity. Chunks are fed strictly in decode order.
   */
  pump(throughFrame: number): void {
    const decoder = this.decoder
    if (!decoder || decoder.state !== 'configured') return

    while (
      this.cursor < this.media.samples.length &&
      this.frames.size + this.pending < LANE_CAPACITY
    ) {
      const sample = this.media.samples[this.cursor]!
      // Stop once we have fed past what is wanted, but only when the lane is
      // already holding useful output — otherwise a GOP whose presentation
      // times run ahead of decode order could stall forever.
      if (frameIndexOf(sample.timestamp) > throughFrame && this.frames.size > 0) break

      try {
        decoder.decode(
          new EncodedVideoChunk({
            type: sample.key ? 'key' : 'delta',
            timestamp: sample.timestamp,
            duration: sample.duration,
            data: sample.data,
          }),
        )
      } catch (err) {
        this.failed = err instanceof Error ? err : new Error(String(err))
        return
      }
      this.pending += 1
      this.cursor += 1
    }
  }

  /** Release frames the playhead has passed. */
  evictBelow(frameIndex: number): void {
    for (const [index, frame] of this.frames) {
      if (index < frameIndex) {
        frame.close()
        this.frames.delete(index)
      }
    }
  }

  dispose(): void {
    this.clearFrames()
    this.disposeDecoder()
    this.primedAt = -1
  }

  private ensureDecoder(): void {
    // A VideoDecoder that hits an error is left in "closed" state, and
    // `configure()` on a closed decoder throws. Returning early here whenever a
    // decoder merely *existed* meant one decode error wedged the lane forever:
    // the throw escaped prime() and update(), the render loop stopped painting,
    // and pressing play re-entered the same throw. Recreate instead.
    if (this.decoder && this.decoder.state !== 'closed' && !this.failed) return

    this.disposeDecoder()
    this.failed = null
    this.decoder = new VideoDecoder({
      output: (frame) => {
        this.pending = Math.max(0, this.pending - 1)
        const index = frameIndexOf(frame.timestamp)
        // A reset can race with in-flight output; drop anything unwanted
        // rather than leaking it.
        const existing = this.frames.get(index)
        if (existing) existing.close()
        this.frames.set(index, frame)
      },
      error: (err) => {
        this.failed = err instanceof Error ? err : new Error(String(err))
      },
    })
  }

  private disposeDecoder(): void {
    const decoder = this.decoder
    this.decoder = null
    if (!decoder) return
    try {
      if (decoder.state !== 'closed') decoder.close()
    } catch {
      /* already closed */
    }
  }

  private clearFrames(): void {
    for (const frame of this.frames.values()) frame.close()
    this.frames.clear()
  }

  /** Last sync sample at or before `frameIndex`, in decode order. */
  private keyframeCursorFor(frameIndex: number): number {
    const targetUs = (frameIndex / VIDEO_FPS) * 1e6
    const { keyframes, samples } = this.media
    let best = keyframes[0] ?? 0
    let lo = 0
    let hi = keyframes.length - 1
    while (lo <= hi) {
      const mid = (lo + hi) >> 1
      const candidate = keyframes[mid]!
      if (samples[candidate]!.timestamp <= targetUs + 1) {
        best = candidate
        lo = mid + 1
      } else {
        hi = mid - 1
      }
    }
    return best
  }
}

function frameIndexOf(timestampUs: number): number {
  return Math.round((timestampUs / 1e6) * VIDEO_FPS)
}

/**
 * Frame-exact video, locked to the audio clock.
 *
 * Rather than asking a `<video>` element to keep up — the approach both
 * previous builds took, correcting with `playbackRate` nudges and periodic
 * seeks — this decodes frames itself and *chooses* which one to present for a
 * given musical position. The consequence is that error cannot accumulate:
 * every frame is selected fresh from the audio clock, so the worst case is a
 * single frame interval of rounding and the expected case is half of that.
 * There is no control loop to settle, nothing to drift, and no correction
 * event visible at the loop seam.
 */
export class FrameScheduler implements FrameSource {
  readonly kind = 'frame-exact' as const

  private primary: DecodeLane
  private standby: DecodeLane
  private lastWantedFrame = 0
  private loopStartFrame = 0
  private loopEndFrame = LAST_LOOP_FRAME
  private loopEnabled = true
  private lastPresentedIndex = -1
  private disposed = false
  private stalledSince = 0
  private recoveries = 0
  private lastError: Error | null = null

  private constructor(private readonly media: DemuxedVideo) {
    this.primary = new DecodeLane(media, media.config)
    this.standby = new DecodeLane(media, media.config)
    this.primary.prime(0)
  }

  get width(): number {
    return this.media.width
  }

  get height(): number {
    return this.media.height
  }

  /**
   * Returns null when WebCodecs cannot handle this stream, so the caller can
   * try the next tier rather than fail.
   *
   * Codec support is checked *before* the file is fetched. Getting that order
   * wrong would mean downloading 31 MB only to discover the browser cannot
   * decode it.
   */
  /**
   * Build a scheduler over already-demuxed media. Exposed so the loop-seam
   * behaviour can be exercised against synthetic media in `npm run verify`,
   * which is otherwise unreachable without a real decoder and a real MP4.
   */
  static fromDemuxed(media: DemuxedVideo): FrameScheduler {
    return new FrameScheduler(media)
  }

  static async tryCreate(
    tier: { url: string; codec: string; width: number; height: number; requireHardware: boolean },
    signal?: AbortSignal,
  ): Promise<FrameScheduler | null> {
    if (typeof VideoDecoder === 'undefined') return null

    const probe: VideoDecoderConfig = {
      codec: tier.codec,
      codedWidth: tier.width,
      codedHeight: tier.height,
      ...(tier.requireHardware ? { hardwareAcceleration: 'prefer-hardware' as const } : {}),
    }

    try {
      const support = await VideoDecoder.isConfigSupported(probe)
      if (!support.supported) return null
    } catch {
      return null
    }

    let media: DemuxedVideo
    try {
      media = await demuxVideo(tier.url, signal)
    } catch {
      return null
    }

    // Re-check with the real in-band configuration, which carries the actual
    // profile and level rather than the guess in the tier table.
    try {
      const support = await VideoDecoder.isConfigSupported(media.config)
      if (!support.supported) return null
    } catch {
      return null
    }

    return new FrameScheduler(media)
  }

  setLoop(start: number, end: number, enabled: boolean): void {
    this.loopStartFrame = Math.max(0, Math.floor(start * VIDEO_FPS))
    this.loopEndFrame = Math.min(
      this.media.samples.length - 1,
      Math.max(this.loopStartFrame + 1, Math.floor(end * VIDEO_FPS)),
    )
    this.loopEnabled = enabled
  }

  acquire(musical: number): CanvasImageSource | null {
    const wanted = this.frameIndexFor(musical)
    const frame = this.primary.get(wanted)
    if (frame) {
      this.lastPresentedIndex = wanted
      return frame
    }

    // Repeating the previous frame is strictly better than blanking while a
    // decode catches up. It is looked up by index rather than held as a
    // reference, because eviction or a lane swap can close the frame behind our
    // back and drawing a closed VideoFrame throws.
    if (this.lastPresentedIndex >= 0) {
      const held = this.primary.get(this.lastPresentedIndex)
      if (held) return held
    }
    this.lastPresentedIndex = -1
    return null
  }

  get presentedId(): number {
    return this.lastPresentedIndex
  }

  update(musical: number, tempo: number, playing: boolean): void {
    if (this.disposed) return
    try {
      this.updateInner(musical, tempo, playing)
    } catch (err) {
      // Nothing in the video path is allowed to stop the render loop or the
      // transport. Record it, tear the lanes down, and let the watchdog rebuild.
      this.lastError = err instanceof Error ? err : new Error(String(err))
      this.rebuildLanes(this.frameIndexFor(musical))
    }
  }

  private updateInner(musical: number, tempo: number, playing: boolean): void {
    const wanted = this.frameIndexFor(musical)

    // A large backwards jump with looping on is the seam; the standby lane has
    // been decoding the loop entry and is ready to take over.
    const wrapped = wanted < this.lastWantedFrame - VIDEO_FPS
    if (wrapped && this.loopEnabled && this.standby.primedAt >= 0) {
      this.swapLanes()
    } else if (wrapped || wanted > this.lastWantedFrame + VIDEO_FPS * 2) {
      // Any other discontinuity is a genuine seek.
      this.primary.prime(wanted)
    }
    this.lastWantedFrame = wanted

    if (this.primary.primedAt < 0) this.primary.prime(wanted)

    // Lookahead scales with tempo so the buffer covers the same wall-clock
    // time at 100 % as it does at 25 %.
    const lookahead = clamp(Math.ceil(0.25 * VIDEO_FPS * tempo), 4, LANE_CAPACITY - 2)

    this.primary.evictBelow(wanted - 1)
    this.primary.pump(wanted + lookahead)

    if (playing && this.loopEnabled) {
      const distanceToSeam = this.loopEndFrame - wanted
      if (distanceToSeam >= 0 && distanceToSeam <= SEAM_LEAD_FRAMES) {
        if (this.standby.primedAt !== this.loopStartFrame) {
          this.standby.prime(this.loopStartFrame)
        }
        this.standby.pump(this.loopStartFrame + lookahead)
      }
    }

    this.checkForStall(wanted, playing)
  }

  /**
   * Rebuild the decode lanes if the clock keeps advancing while no frame for
   * the wanted position ever arrives.
   *
   * This is a backstop, not the primary mechanism. A decoder can close for
   * reasons outside our control — hardware decoder slots exhausted, a GPU
   * driver reset, the OS reclaiming resources on a backgrounded tab — and the
   * player must recover from all of them by itself rather than requiring a
   * page reload.
   */
  private checkForStall(wanted: number, playing: boolean): void {
    if (!playing || this.primary.has(wanted)) {
      this.stalledSince = 0
      return
    }
    const now = performance.now()
    if (this.stalledSince === 0) {
      this.stalledSince = now
      return
    }
    if (now - this.stalledSince < STALL_TIMEOUT_MS) return

    this.recoveries += 1
    this.stalledSince = 0
    this.rebuildLanes(wanted)
  }

  private rebuildLanes(wanted: number): void {
    this.primary.dispose()
    this.standby.dispose()
    this.primary = new DecodeLane(this.media, this.media.config)
    this.standby = new DecodeLane(this.media, this.media.config)
    this.lastPresentedIndex = -1
    this.primary.prime(wanted)
  }

  seek(musical: number): void {
    this.lastPresentedIndex = -1
    this.primary.prime(this.frameIndexFor(musical))
    this.standby.dispose()
    this.standby = new DecodeLane(this.media, this.media.config)
  }

  get diagnostics(): SyncDiagnostics {
    const err = this.lastError ?? this.primary.error ?? this.standby.error
    const halfFrameMs = (1000 / VIDEO_FPS) / 2
    const recovered = this.recoveries > 0 ? ` Recovered from ${this.recoveries} decoder stall(s).` : ''
    return {
      kind: 'WebCodecs frame-exact',
      errorMs: halfFrameMs,
      ready: this.primary.size > 0,
      detail: err
        ? `Decoder error (recovering): ${err.message}`
        : `Locked to audio clock; bounded at ${halfFrameMs.toFixed(1)} ms, no drift.${recovered}`,
    }
  }

  dispose(): void {
    this.disposed = true
    this.primary.dispose()
    this.standby.dispose()
  }

  /**
   * Hand over to the lane that has been pre-decoding the loop entry.
   *
   * The lane *objects* are swapped wholesale. An earlier version moved the
   * `VideoDecoder` from one lane into another, which looked equivalent and was
   * not: the decoder's `output` callback closes over the lane that built it, so
   * a moved decoder went on writing frames into the orphaned lane and
   * decrementing its `pending`. The receiving lane's frame map then never grew
   * past the copied snapshot and its `pending` never came back down, which
   * stopped `pump()` outright — the picture froze exactly one loop in.
   *
   * A decoder and its output callback belong together. Do not separate them.
   */
  private swapLanes(): void {
    const retired = this.primary
    this.primary = this.standby
    this.standby = new DecodeLane(this.media, this.media.config)

    // The retired lane is about to close its frames, so drop any index that
    // still refers into it.
    this.lastPresentedIndex = -1

    retired.dispose()
  }

  private frameIndexFor(musical: number): number {
    const raw = Math.floor(musical * VIDEO_FPS)
    const ceiling = this.loopEnabled ? this.loopEndFrame : this.media.samples.length - 1
    return clamp(raw, 0, ceiling)
  }
}
